/**
 * 메신저 및 SNS 인앱 브라우저(WebView) 환경 여부를 판정합니다.
 * 구글 OAuth가 보안 정책(disallowed_useragent, 403)으로 차단되는 환경을 감지하기 위한 유틸입니다.
 * 
 * 일반 Chrome, Safari, Samsung Internet, 독립 Whale 브라우저 등은 정상 브라우저로 취급하여
 * 오판으로 인해 구글 로그인이 차단되지 않도록 안전하게 판정합니다.
 * 
 * @returns {boolean} 인앱 브라우저이면 true, 일반 브라우저이거나 판정 불가 시 false
 */
export function isInAppBrowser() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return false;
    }

    const ua = window.navigator.userAgent.toLowerCase();

    // 1. 주요 메신저 및 SNS 앱 내장 웹뷰 시그니처 (대소문자 무시)
    // - kakaotalk: 카카오톡
    // - line/ 또는 line\b: 라인 메신저
    // - instagram: 인스타그램
    // - fban, fbav: 페이스북 메신저/앱
    // - naver(inapp 또는 naverapp: 네이버 앱 웹뷰
    // - band/: 네이버 밴드
    // - micromessenger: 위챗
    // - bytedance, musical_ly, tiktok: 틱톡 및 바이트댄스 앱
    const inAppSignatures = [
      'kakaotalk',
      'instagram',
      'line/',
      'fban',
      'fbav',
      'naver(inapp',
      'naverapp',
      'band/',
      'micromessenger',
      'bytedance',
      'musical_ly',
      'tiktok',
      // 메신저·SNS
      'telegram',
      'whatsapp',
      'viber',
      'qq/',
      'zalo',
      'threads',
      'twitter',
      'snapchat',
      'pinterest',
      'linkedin',
      'discord',
      'slack',
      'teams/',
      'vkandroid',
      'vkclient',
      // 동남아 슈퍼앱·커머스
      'grab/',
      'gojek',
      'shopee',
      'lazada',
      'tokopedia',
      // 중국·기타 아시아
      'alipayclient',
      'weibo',
      'dingtalk',
      'ucbrowser',
      'xhsdiscover',
      // 한국·일본·인도·기타
      'daumapps',
      'rakuten',
      'yjapp',
      'jp.co.yahoo',
      'paytm',
      'truecaller',
    ];

    for (let i = 0; i < inAppSignatures.length; i++) {
      if (ua.includes(inAppSignatures[i])) {
        return true;
      }
    }

    // 2. 정규식 기반 보조 검사
    // - 네이버 앱 웹뷰: NAVER(inapp; ... ) 또는 [^\w]naver[^\w]
    if (/naver\(inapp/i.test(ua) || /\bnaverapp\b/i.test(ua)) {
      return true;
    }

    // - 네이버 인앱 웨일 웹뷰 (일반 독립 데스크톱/모바일 웨일은 'whale/버전'만 존재하고 naver inapp 표기가 없음)
    if (/naver\(inapp.*whale/i.test(ua)) {
      return true;
    }

    // - 페이스북 인앱 단독 식별자
    if (/\b(fban|fbav)\b/i.test(ua)) {
      return true;
    }

    // - 밴드 단독 식별자
    if (/\bband\//i.test(ua)) {
      return true;
    }

    return false;
  } catch {
    // 판정 실패 시 안전하게 일반 브라우저로 간주하여 정상 사용자 차단 방지
    return false;
  }
}

/**
 * Android 운영체제 환경 여부를 판정합니다.
 * @returns {boolean} 안드로이드 환경이면 true, 아니거나 판정 불가 시 false
 */
export function isAndroid() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return false;
    }
    return window.navigator.userAgent.toLowerCase().includes('android');
  } catch {
    return false;
  }
}

/**
 * iOS 운영체제(iPhone, iPad, iPod 및 iPadOS 위장 데스크톱 모드) 환경 여부를 판정합니다.
 * @returns {boolean} iOS 환경이면 true, 아니거나 판정 불가 시 false
 */
export function isIOS() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return false;
    }
    const ua = window.navigator.userAgent.toLowerCase();
    const isStandardIOS = /iphone|ipad|ipod/i.test(ua);
    const isIPadOS = /macintosh/i.test(ua) && Boolean(window.navigator.maxTouchPoints && window.navigator.maxTouchPoints > 1);

    return isStandardIOS || isIPadOS;
  } catch {
    return false;
  }
}

/**
 * PWA 홈 화면 추가(standalone 디스플레이 모드) 상태 여부를 판정합니다.
 * @returns {boolean} 홈 화면 추가 상태이면 true, 아니거나 판정 불가 시 false
 */
export function isStandalone() {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    const isIOSStandalone = window.navigator && window.navigator.standalone === true;
    const isDisplayModeStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches === true;

    return Boolean(isIOSStandalone || isDisplayModeStandalone);
  } catch {
    return false;
  }
}

/**
 * Windows 운영체제 환경 여부를 판정합니다.
 * @returns {boolean} Windows 환경이면 true, 아니거나 판정 불가 시 false
 */
export function isWindows() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return false;
    }
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.includes('windows') || ua.includes('win32');
  } catch {
    return false;
  }
}

/**
 * macOS 운영체제 환경 여부를 판정합니다. (iPadOS 데스크톱 모드 제외)
 * @returns {boolean} macOS 환경이면 true, 아니거나 판정 불가 시 false
 */
export function isMac() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return false;
    }
    if (isIOS()) {
      return false;
    }
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.includes('macintosh') || ua.includes('mac os');
  } catch {
    return false;
  }
}

/**
 * 접속 브라우저 식별자를 반환합니다.
 * @returns {'samsung' | 'whale' | 'edge' | 'opera' | 'firefox' | 'chrome' | 'safari' | ''} 브라우저 식별자
 */
export function getBrowserName() {
  try {
    if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) {
      return '';
    }
    const ua = window.navigator.userAgent.toLowerCase();

    // 1) samsungbrowser
    if (ua.includes('samsungbrowser')) return 'samsung';
    // 2) whale
    if (ua.includes('whale')) return 'whale';
    // 3) edg
    if (ua.includes('edg')) return 'edge';
    // 4) opr 또는 opera
    if (ua.includes('opr') || ua.includes('opera')) return 'opera';
    // 5) firefox 또는 fxios
    if (ua.includes('firefox') || ua.includes('fxios')) return 'firefox';
    // 6) crios (iOS Chrome)
    if (ua.includes('crios')) return 'chrome';
    // 7) chrome
    if (ua.includes('chrome')) return 'chrome';
    // 8) safari
    if (ua.includes('safari')) return 'safari';

    // 9) 그 외 미판별 시 빈 문자열
    return '';
  } catch {
    return '';
  }
}


